import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';
import type { AssignmentStatus } from '@/types/grading';

interface Assignment {
  id: string;
  student_name: string | null;
  student_class: string | null;
  grade_level: string;
  status: AssignmentStatus;
  submit_code: string | null;
  created_at: string;
  grading_results: Array<{
    id: string;
    total_score: number;
    max_total_score: number;
    confidence: number;
    needs_review: boolean;
  }>;
  student_submissions: Array<{ id: string }>;
}

const statusColors: Record<AssignmentStatus, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default',
  ready_for_submission: 'info',
  pending: 'default',
  processing: 'primary',
  completed: 'success',
  failed: 'error',
  review: 'warning',
};

const statusLabels: Record<AssignmentStatus, string> = {
  draft: '草稿',
  ready_for_submission: '等待提交',
  pending: '等待中',
  processing: '批改中',
  completed: '已完成',
  failed: '失敗',
  review: '待覆核',
};

export function AssignmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDelete = async (e: React.MouseEvent, assignmentId: string) => {
    e.stopPropagation();
    if (!user || !confirm('確定要刪除此作業？此操作無法復原。')) return;

    setDeleting(assignmentId);
    try {
      const response = await fetch(`/api/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'x-teacher-id': user.id },
      });
      if (response.ok) {
        setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      }
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchAssignments = async () => {
      const { data, error } = await supabase
        .from('assignments')
        .select(
          `
          id,
          student_name,
          student_class,
          grade_level,
          status,
          submit_code,
          created_at,
          grading_results (
            id,
            total_score,
            max_total_score,
            confidence,
            needs_review
          ),
          student_submissions (
            id
          )
        `
        )
        .eq('teacher_id', user.id)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setAssignments(data as Assignment[]);
      }
      setLoading(false);
    };

    fetchAssignments();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('assignments')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'assignments',
          filter: `teacher_id=eq.${user.id}`,
        },
        () => {
          fetchAssignments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          作業批改
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/assignments/new')}
        >
          新增作業
        </Button>
      </Box>

      {assignments.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary" gutterBottom>
              尚未有作業記錄
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/assignments/new')}
              sx={{ mt: 2 }}
            >
              上傳第一份作業
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>年級</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>提交碼</TableCell>
                <TableCell>提交數</TableCell>
                <TableCell>日期</TableCell>
                <TableCell align="center" sx={{ width: 60 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment) => {
                const result = assignment.grading_results[0];
                return (
                  <TableRow
                    key={assignment.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      assignment.status === 'draft'
                        ? navigate(`/assignments/new?id=${assignment.id}`)
                        : navigate(`/assignments/${assignment.id}`)
                    }
                  >
                    <TableCell>{assignment.grade_level}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[assignment.status]}
                        color={statusColors[assignment.status] as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {assignment.submit_code ? (
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                          {assignment.submit_code}
                        </Typography>
                      ) : assignment.status === 'draft' ? (
                        <Typography variant="body2" color="info.dark" fontWeight={600}>
                          繼續編輯
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {(assignment.student_submissions?.length || 0) > 0 ? (
                        <Chip size="small" label={`${assignment.student_submissions.length} 份`} variant="outlined" />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(assignment.created_at).toLocaleDateString('zh-HK')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="刪除">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleting === assignment.id}
                          onClick={(e) => handleDelete(e, assignment.id)}
                        >
                          {deleting === assignment.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

